import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { Field } from '@sitecore-jss/sitecore-jss-nextjs';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-react';

import { useSiteSettings } from '@gn-audio/jss-components';
import {
  DEFAULT_SC_LOCALE,
  Family,
  getFamily,
  getFamilyAttributes,
  getFamilyPrice,
  getGroup,
  getGroupAttributes,
  getGroupPrice,
  getLocaleWithRegion,
  getSharedProperty,
  Group,
  isPricingApiConfigured,
  ProductWithAttributes,
} from '@gn-audio/jss-core';

import { mapToStandardPriceModel } from 'api/pricesApi/priceMapper';
import { StandardPriceModel } from 'api/pricesApi/pricesModel';
import { logError } from 'utils/logger';

export interface ICProductContext {
  productId?: string;
  productName?: string;
  productType?: string;
  attributes?: ProductWithAttributes | null;
  product?: Family | Group | null;
  price?: StandardPriceModel | null;
}
export const PageProductContext = createContext<ICProductContext>({});

export const usePageProduct = () => useContext(PageProductContext);

export const useProductField = () => {
  const { sitecoreContext } = useSitecoreContext();

  const product = sitecoreContext.route?.fields
    ? getSharedProperty(sitecoreContext.route?.fields as { Product: Field<string> }, 'Product')
        ?.value
    : undefined;

  const [productId, productType, productName] = (product ?? '').split(':');
  return { productId, productType, productName };
};

export const PageProductProvider = ({ children }: { children: ReactNode }) => {
  const { productId, productType, productName } = useProductField();
  const { ecom } = useSiteSettings();
  const { sitecoreContext } = useSitecoreContext();
  const language = getLocaleWithRegion(sitecoreContext.language ?? DEFAULT_SC_LOCALE);

  const [attributes, setAttributes] = useState<ProductWithAttributes | null>(null);
  const [product, setProduct] = useState<Family | Group | null>(null);
  const [price, setPrice] = useState<StandardPriceModel | null>(null);

  useEffect(() => {
    if (!productId || !productType) return;

    const fetchAttributes = async () => {
      try {
        if (productType === 'Group') {
          const result = await getGroupAttributes(+productId, language);
          setAttributes(result);
        } else if (productType === 'Family') {
          const result = await getFamilyAttributes(+productId, language);
          setAttributes(result?.length ? result[0] : null);
        }
      } catch (error) {
        logError('Server error', error);
      }
    };

    fetchAttributes();
  }, [productId, productType, language]);

  useEffect(() => {
    if (!productId || !productType) return;

    const fetchProduct = async () => {
      try {
        if (productType === 'Group') {
          const result = await getGroup(+productId, language);
          setProduct(result);
        } else if (productType === 'Family') {
          const result = await getFamily(+productId, language);
          setProduct(result);
        }
      } catch (error) {
        logError('Server error', error);
      }
    };

    fetchProduct();
  }, [productId, productType, language]);

  useEffect(() => {
    if (!isPricingApiConfigured() || !productId || !productType || !ecom?.ecomEnabled) return;

    const fetchPrice = async () => {
      try {
        let priceData;
        if (productType === 'Group') {
          priceData = await getGroupPrice(+productId);
        } else if (productType === 'Family') {
          priceData = await getFamilyPrice(+productId);
        }
        setPrice(priceData ? mapToStandardPriceModel(priceData) : null);
      } catch (error) {
        logError('Server error', error);
      }
    };

    fetchPrice();
  }, [productId, productType, ecom]);

  const productContext = {
    productId,
    productType,
    productName,
    product,
    attributes,
    price,
  };

  return (
    <PageProductContext.Provider value={productContext}>{children}</PageProductContext.Provider>
  );
};
